import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "./App.css";
import { LangProvider, LANGS, isRTL } from "./i18n";
import { AuthProvider } from "./context/AuthContext";
import { useLenis } from "./components/motion";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Chefs from "./pages/Chefs";
import ChefProfile from "./pages/ChefProfile";
import Experiences from "./pages/Experiences";
import { HowItWorks, About, PrivateDining, Faq, Legal } from "./pages/StaticPages";
import Inquiry from "./pages/Inquiry";
import BecomeAChef from "./pages/BecomeAChef";
import GiftCards from "./pages/GiftCards";
import { Journal, JournalArticle } from "./pages/Journal";
import Auth from "./pages/Auth";
import Booking from "./pages/Booking";
import Account from "./pages/Account";
import PaymentStatus from "./pages/PaymentStatus";

const VALID = LANGS.map((l) => l.code);

function LangLayout() {
  const { lang } = useParams();
  const { pathname } = useLocation();
  useLenis();

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = isRTL(lang) ? "rtl" : "ltr";
  }, [lang]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  if (!VALID.includes(lang)) return <Navigate to="/fr" replace />;

  return (
    <LangProvider lang={lang}>
      <div className="grain min-h-screen flex flex-col bg-ink">
        <Nav />
        <main className="flex-1">
          <Outlet />
        </main>
        <Footer />
      </div>
    </LangProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" theme="dark" toastOptions={{ style: { background: "#121212", color: "#F9F6F0", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 0 } }} />
        <Routes>
          <Route path="/" element={<Navigate to="/fr" replace />} />
          <Route path="/:lang" element={<LangLayout />}>
            <Route index element={<Home />} />
            <Route path="chefs" element={<Chefs />} />
            <Route path="chef/:slug" element={<ChefProfile />} />
            <Route path="experiences" element={<Experiences />} />
            <Route path="private-dining" element={<PrivateDining />} />
            <Route path="events" element={<Inquiry type="events" key="events" />} />
            <Route path="corporate" element={<Inquiry type="corporate" key="corporate" />} />
            <Route path="bespoke" element={<Inquiry type="bespoke" key="bespoke" />} />
            <Route path="contact" element={<Inquiry type="contact" key="contact" />} />
            <Route path="how-it-works" element={<HowItWorks />} />
            <Route path="become-a-chef" element={<BecomeAChef />} />
            <Route path="about" element={<About />} />
            <Route path="faq" element={<Faq />} />
            <Route path="gift-cards" element={<GiftCards />} />
            <Route path="journal" element={<Journal />} />
            <Route path="journal/:slug" element={<JournalArticle />} />
            <Route path="terms" element={<Legal page="terms" key="terms" />} />
            <Route path="privacy" element={<Legal page="privacy" key="privacy" />} />
            <Route path="cancellation" element={<Legal page="cancellation" key="cancellation" />} />
            <Route path="login" element={<Auth mode="login" />} />
            <Route path="register" element={<Auth mode="register" />} />
            <Route path="book" element={<Booking />} />
            <Route path="account" element={<Account />} />
            <Route path="payment/status" element={<PaymentStatus />} />
            <Route path="*" element={<Navigate to={`.`} replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/fr" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
